import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/store';
import { isAdminRole, isSuperAdminRole } from '@/utils/roles';

export function useAuth() {
  const navigate = useNavigate();
  const {
    user,
    isAuthenticated,
    isLoading,
    error,
    login,
    logout,
    register,
    changePassword,
    clearError,
  } = useAuthStore();

  const isAdmin = isAdminRole(user?.role);
  const isSuperAdmin = isSuperAdminRole(user?.role);

  const handleLogin = useCallback(
    async (username: string, password: string) => {
      try {
        const response = await login({ username, password });
        if (response.user.passwordResetRequired) {
          navigate('/change-password');
        } else if (isAdminRole(response.user.role)) {
          navigate('/admin/dashboard');
        } else {
          navigate('/dashboard');
        }
      } catch {}
    },
    [login, navigate],
  );

  const handleLogout = useCallback(async () => {
    await logout();
    navigate('/login');
  }, [logout, navigate]);

  const handleRegister = useCallback(
    async (data: Parameters<typeof register>[0]) => {
      await register(data);
      navigate('/login', {
        state: {
          message: 'Registration successful. Please wait for admin approval.',
        },
      });
    },
    [register, navigate],
  );

  const handleChangePassword = useCallback(
    async (data: Parameters<typeof changePassword>[0]) => {
      await changePassword(data);
      if (isAdmin) {
        navigate('/admin/dashboard');
      } else {
        navigate('/dashboard');
      }
    },
    [changePassword, navigate, isAdmin],
  );

  return {
    user,
    isAuthenticated,
    isLoading,
    error,
    isAdmin,
    isSuperAdmin,
    login: handleLogin,
    logout: handleLogout,
    register: handleRegister,
    changePassword: handleChangePassword,
    clearError,
  };
}

export default useAuth;
